import { wallOpeningOpening3 } from "building-common/openings/openingQuery";
import type { Building } from "building-types/building/buildingTypes";
import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import { FinishClipping } from "building-types/casing/casingTypes";
import type { SwingSideGenerator, DoorDescriptor } from "building-types/doors/doorTypes";
import { SwingSide } from "building-types/doors/doorTypes";
import type {
  DoorPlacementDeprecated,
  OpeningDescriptor,
  WindowPlacementDeprecated,
} from "building-types/openings/opening";
import type { OpeningResult } from "building-types/openings/openingResultTypes";
import { type OpeningProfileLoops2, type OpeningSide } from "building-types/openings/openingTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { Room } from "building-types/rooms/roomTypes";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import type { InteriorDoorCasingSettings } from "building-types/settings/interiorDoorCasingSettings";
import { SettingsType } from "building-types/settings/settingsType";
import {
  ApronCasingType,
  HeadCasingType,
  SideCasingType,
  StoolCasingType,
} from "building-types/settings/settingsTypesEnum";
import type { WallSidePathDomain, WallSideVerticalDomain } from "building-types/walls/wallSideTypes";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Wall } from "building-types/walls/wallTypes";
import type { LevelInches, WorldInches } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { levelInchesNew, worldInchesNew } from "common/utils/nominalNumbers";
import { resolvePlacementFromMatchExpression } from "doc/elements/placement/edgePlacementMatchExpressionUtils";
import { angleClean } from "geom/angle";
import { closestParamLine2, closestParamNormalizedLine2 } from "geom/closestPoint";
import { ANGLE_EPSILON, DIST_EPSILON, PI, TWO_PI, WindingDir } from "geom/core";
import type { Segment3PlaneIntersection } from "geom/intersect";
import { intersectPolyLine3Plane } from "geom/intersect";
import { IntersectionKind } from "geom/intersectRay2";
import type { Line2Segment } from "geom/line2";
import { line2Len, line2Normal, line2Vec } from "geom/line2";
import type { PlanarGraph } from "geom/planarGraph";
import {
  planarGraphAdjacentFace,
  planarGraphAdjacentHalfEdge,
  planarGraphHalfEdgeEnd,
  planarGraphHalfEdgeStart,
  planarGraphVertexStripToPolyLine2,
} from "geom/planarGraph";
import { planeXYAtZ } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonUnion } from "geom/polyclip/polyClip";
import { type EdgeRef, type PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import {
  polyLine2ConvexHull,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2Length,
  polyLine2OppositeEdgeIndex,
  polyLine2Reverse,
  polyLine2Shift,
  polyLine2WindingDir,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3AABB3 } from "geom/polyLine3";
import { rect2ByThreePoints, rect2ByTwoPoints } from "geom/rect2";
import type { Rect2 } from "geom/rect2Types";
import { denormalizeParam } from "geom/utils";
import type { Vec2 } from "geom/vec2";
import { vec2AngleBetween } from "geom/vec2";
import { edgeHostedEntityGetLayerEdgeLength } from "../edgePlacementUtils";
import { checkOrientationWithMode } from "../streetOrientation";
import { wallSidePathDomainNew, wallSideVerticalDomainNew } from "../walls/wallSideUtils";
import type { OpeningPlacementFuncOptions, OpeningPlacementFuncOptionsLite } from "./openingPlacementFuncOptions";

export function filterDifferentRoomBlocks(room: Room | undefined, ajdRoom: Room | undefined): boolean {
  const startBlockId = room?.parentId;
  const endBlockId = ajdRoom?.parentId;

  return startBlockId === endBlockId;
}

export function swingSideMirror(swingSide?: SwingSide | SwingSideGenerator) {
  if (swingSide === SwingSide.LEFT) {
    return SwingSide.RIGHT;
  } else if (swingSide === SwingSide.RIGHT || swingSide === SwingSide.NONE || swingSide === undefined) {
    return SwingSide.LEFT;
  }
  return swingSide;
}

export function doorSwingsToOppositeWall(opts: OpeningPlacementFuncOptions, doorDescriptor: DoorDescriptor) {
  const planarGraph = opts.planarGraph.planarGraph;

  // try to fix any issues with door direction
  const parentFace = planarGraph.halfEdgeParentFace[opts.halfEdgeIndex];
  const faceHalfEdges = planarGraph.faceHalfEdges[parentFace];
  const faceOutline = planarGraphVertexStripToPolyLine2(planarGraph, planarGraph.faceVertices[parentFace]);

  const halfEdgeIndexInParentFace = faceHalfEdges.indexOf(opts.halfEdgeIndex);

  const res = polyLine2OppositeEdgeIndex(faceOutline, halfEdgeIndexInParentFace, 0.5, true);
  if (!res) {
    return false;
  }

  const [, dist] = res;

  const finalDoorType = doorDescriptor["id"] !== undefined ? doorDescriptor : doorDescriptor[opts.level.topOfWall];

  return dist < finalDoorType.width;
}

export function openingPlacementFuncOptionsInvert(opts: OpeningPlacementFuncOptions): OpeningPlacementFuncOptions {
  let { roomStart, roomEnd, halfEdgeIndex, wallIndex, streetOrientation } = opts;
  const { planarGraph } = opts;

  const t = roomStart;
  roomStart = roomEnd;
  roomEnd = t;

  halfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);
  const edgeRef = planarGraph.halfEdgeIndexToPolygonEdgeRef(halfEdgeIndex);
  wallIndex = edgeRef?.index;

  streetOrientation = opts.perimeterStreetOrientationTable[halfEdgeIndex];

  return {
    ...opts,
    wallIndex,
    roomStart,
    roomEnd,
    halfEdgeIndex,
    streetOrientation,
  };
}

export function openingPlacementFuncOptionsLiteInvert(
  opts: OpeningPlacementFuncOptionsLite,
): OpeningPlacementFuncOptionsLite {
  let { roomStart, roomEnd, halfEdgeIndex, wallIndex, streetOrientation } = opts;
  const { planarGraph } = opts;

  const t = roomStart;
  roomStart = roomEnd;
  roomEnd = t;

  halfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);
  const edgeRef = planarGraph.halfEdgeIndexToPolygonEdgeRef(halfEdgeIndex);
  wallIndex = edgeRef?.index;

  streetOrientation = opts.perimeterStreetOrientationTable[halfEdgeIndex];

  return {
    ...opts,
    wallIndex,
    roomStart,
    roomEnd,
    halfEdgeIndex,
    streetOrientation,
  };
}

export function locateHalfEdgeAtWallStripParamForDeprecatedOpenings(
  room: Room,
  roomEdgeIndex: number,
  param: number,
  paramNormalized: boolean,
  roomIndex: RoomIndex,
) {
  const { outline } = room;
  const roomIndexOnLevel = roomIndex.roomLevelIndices[room.id];
  const polygonPlanarGraph = roomIndex.planarGraphs[room.level.id];
  const { planarGraph } = polygonPlanarGraph;

  if (param < 0) {
    if (paramNormalized) {
      param = param + 1.0;
    } else {
      param = param + polyLine2EdgeLength(outline, roomEdgeIndex);
    }
  }

  const [x, y] = polyLine2EdgeEval(outline, roomEdgeIndex, param, paramNormalized);

  const subwalls = polygonPlanarGraph.polygonToPlanarGraph[roomIndexOnLevel][roomEdgeIndex];
  if (!subwalls) {
    return undefined;
  }

  // for each sub-wall of wall
  for (let i = 0; i < subwalls.length; i++) {
    const { index, edgeIndex } = subwalls[i];
    const halfEdgeIndex = planarGraph.faceHalfEdges[index][edgeIndex];

    const [ax, ay] = planarGraphHalfEdgeStart(planarGraph, halfEdgeIndex);
    const [bx, by] = planarGraphHalfEdgeEnd(planarGraph, halfEdgeIndex);

    if (paramNormalized) {
      const t = closestParamNormalizedLine2(ax, ay, bx, by, x, y, false);
      if (t >= 0 && t <= 1.0) {
        return {
          param: t,
          halfEdgeIndex,
          roomEnd: roomIndex.adjacentRoomOnEdge(room, roomEdgeIndex, i),
        };
      }
    } else {
      const t = closestParamLine2(ax, ay, bx, by, x, y, false);

      if (t >= 0 && t <= line2Len(ax, ay, bx, by)) {
        return {
          param: t,
          halfEdgeIndex,
          roomEnd: roomIndex.adjacentRoomOnEdge(room, roomEdgeIndex, i),
        };
      }
    }
  }

  return null;
}

export function locateHalfEdgeAtWallStripParam(
  room: Room,
  roomEdgeIndex: number,
  param: number,
  subwalls: EdgeRef[],
  polygonPlanarGraph: PolygonPlanarGraph,
  levelRooms: Room[],
) {
  if (!subwalls) {
    return undefined;
  }
  const { outline } = room;
  const planarGraph = polygonPlanarGraph.planarGraph;
  const [x, y] = polyLine2EdgeEval(outline, roomEdgeIndex, param, false);

  // for each sub-wall of wall
  for (let i = 0; i < subwalls.length; i++) {
    const { index, edgeIndex } = subwalls[i];
    const halfEdgeIndex = planarGraph.faceHalfEdges[index][edgeIndex];

    const [ax, ay] = planarGraphHalfEdgeStart(planarGraph, halfEdgeIndex);
    const [bx, by] = planarGraphHalfEdgeEnd(planarGraph, halfEdgeIndex);

    const t = closestParamLine2(ax, ay, bx, by, x, y, false);
    if (t + DIST_EPSILON >= 0 && t - DIST_EPSILON <= line2Len(ax, ay, bx, by)) {
      const adjFace = planarGraphAdjacentFace(planarGraph, index, edgeIndex);
      const polygonMap = polygonPlanarGraph.planarGraphToPolygon[adjFace];
      const roomEnd = polygonMap.length !== 0 && polygonMap[0] ? levelRooms[polygonMap[0].index] : undefined;

      return {
        halfEdgeIndex,
        x,
        y,
        roomEnd,
        t,
      };
    }
  }

  return null;
}

export function getWallFromEdgeRef(edgeRef: EdgeRef, planarGraph: PlanarGraph, wallSystem: WallSystem): Wall {
  const { index, edgeIndex } = edgeRef;
  const halfEdgeIndex = planarGraph.faceHalfEdges[index][edgeIndex];
  const wallId = wallSystem.halfEdgeToWall[halfEdgeIndex];
  return wallSystem.walls[wallId];
}

export function isDeprecatedOpening(
  opening: OpeningDescriptor,
): opening is DoorPlacementDeprecated | WindowPlacementDeprecated {
  return opening.type === "WINDOW" || opening.type === "DOOR";
}

export function isOpeningEntity(opening: any): opening is OpeningDescriptor {
  return opening.entityType === "OPENING";
}

export function getHostEdgeLengthForDeprecatedOpening(
  opening: DoorPlacementDeprecated | WindowPlacementDeprecated,
  room: Room,
  building: Pick<Building, "walls">,
) {
  const {
    level: { id: levelId },
  } = room;
  const { wallIndex } = building.walls[levelId];
  const edgeIndex = room.edgeIds.indexOf(opening.edgeId);
  const wallStrip = wallIndex.wallStripFromRoomEdgeIndex(room, edgeIndex);
  if (!wallStrip) {
    return 0;
  }
  const [startX, startY, endX, endY] = wallStrip.baseline(true, true);
  return line2Len(startX, startY, endX, endY);
}

export function getHostEdgeLengthForOpening(
  opening: OpeningDescriptor,
  room: Room,
  building: Pick<Building, "roomIndex" | "wallMasses" | "mirror" | "walls">,
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
): number {
  if (isDeprecatedOpening(opening)) {
    return getHostEdgeLengthForDeprecatedOpening(opening, room, { walls: building.walls });
  }

  const openingNoExpr = resolvePlacementFromMatchExpression(opening, definitions, optionChoices);
  return edgeHostedEntityGetLayerEdgeLength(openingNoExpr, room, building);
}

export function wallOpeningBottomZ(opening: WallOpening) {
  const front = wallOpeningOpening3(opening, "FRONT", false);
  assertIsNotUndefined(front);
  const back = wallOpeningOpening3(opening, "BACK", false);
  assertIsNotUndefined(back);
  const aabb3FrontOutline = polyLine3AABB3(front);
  const aabb3BackOutline = polyLine3AABB3(back);
  return worldInchesNew(Math.min(aabb3FrontOutline.minZ, aabb3BackOutline.minZ));
}

export function rectilinearOpeningProfile({
  start,
  bottom = levelInchesNew(0),
  end,
  top,
  streetOrientation,
  exteriorCasingSettings,
}: {
  start: number;
  bottom?: LevelInches;
  end: number;
  top: LevelInches;
  streetOrientation: StreetOrientation;
  exteriorCasingSettings?: ExteriorDoorCasingSettings | ExteriorWindowCasingSettings;
}): OpeningProfileLoops2 {
  const openingRectangular = rect2ByTwoPoints(start, bottom, end, top);

  // by default, do no clipping and loops are the same
  if (!(exteriorCasingSettings && settingsClipFinish(exteriorCasingSettings))) {
    return {
      openingRect2: openingRectangular,
      opening2: openingRectangular,
      openingCasing2: openingRectangular,
      openingOffsets2: {},
      openingSideLayers: {},
    };
  }

  const { top: casingTop, bottom: casingBottom } = casingAdjustedVerticalFinishDomain(
    streetOrientation,
    wallSideVerticalDomainNew(top, bottom),
    exteriorCasingSettings,
  );
  const { startT: casingStartParam, endT: casingEndParam } = casingAdjustedHorizontalFinishDomain(
    streetOrientation,
    wallSidePathDomainNew(start, end),
    exteriorCasingSettings,
  );

  return {
    openingRect2: openingRectangular,
    opening2: openingRectangular,
    openingCasing2: rect2ByTwoPoints(casingStartParam, casingBottom, casingEndParam, casingTop),
    openingOffsets2: {},
    openingSideLayers: {},
  };
}

export function casingAdjustedHorizontalFinishDomain(
  streetOrientation: StreetOrientation,
  domain: WallSidePathDomain,
  settings: ExteriorDoorCasingSettings | ExteriorWindowCasingSettings,
): WallSidePathDomain {
  const {
    casingOrientation = StreetOrientationMode.ALL_FACES,
    sideCasingType = SideCasingType.DISABLED,
    sideCasingFinishClipping = FinishClipping.NO_CLIPPING,
  } = settings;

  if (sideCasingFinishClipping === FinishClipping.NO_CLIPPING) {
    return domain;
  }
  if (sideCasingType === SideCasingType.DISABLED) {
    return domain;
  }
  if (!clipsHorizontally(settings)) {
    return domain;
  }
  if (!checkOrientationWithMode(streetOrientation, casingOrientation)) {
    return domain;
  }
  const { startT: inputStart, endT: inputEnd } = domain;
  const start = inputStart - (settings.sideCasingWidth ?? 0);
  const end = inputEnd + (settings.sideCasingWidth ?? 0);

  return wallSidePathDomainNew(start, end);
}

export function casingAdjustedVerticalFinishDomain(
  streetOrientation: StreetOrientation = StreetOrientation.UNDEFINED,
  domain: WallSideVerticalDomain,
  settings: ExteriorDoorCasingSettings | ExteriorWindowCasingSettings,
): WallSideVerticalDomain {
  const { casingOrientation = StreetOrientationMode.ALL_FACES } = settings;

  if (!checkOrientationWithMode(streetOrientation, casingOrientation)) {
    return domain;
  }
  if (!clipsVertically(settings)) {
    return domain;
  }

  const { top: inputTop, bottom: inputBottom } = domain;

  let bottom = inputBottom;
  let top = inputTop;

  if (
    settings.headCasingFinishClipping === FinishClipping.CLIP_FINISH &&
    settings.headCasingType === HeadCasingType.TRIM
  ) {
    top = levelInchesNew(top + settings.headCasingHeight);
  }

  if (
    settings.apronCasingFinishClipping === FinishClipping.CLIP_FINISH &&
    settings.apronCasingType !== ApronCasingType.DISABLED
  ) {
    bottom = levelInchesNew(inputBottom - (settings.apronHeight ?? 0));
  }

  if (settings.type === SettingsType.EXTERIOR_WINDOW_CASING_SETTINGS) {
    if (
      settings.stoolCasingFinishClipping === FinishClipping.CLIP_FINISH &&
      settings.stoolCasingType !== StoolCasingType.DISABLED
    ) {
      bottom = levelInchesNew(Math.min(bottom, inputBottom - (settings.stoolCasingThickness ?? 0)));
    }
  }

  return wallSideVerticalDomainNew(top, bottom);
}

export function settingsHasCasing(settings: ExteriorDoorCasingSettings | ExteriorWindowCasingSettings): boolean {
  if (!settings) {
    return false;
  }
  const {
    sideCasingType = SideCasingType.DISABLED,
    headCasingType = HeadCasingType.NONE,
    apronCasingType = ApronCasingType.DISABLED,
  } = settings;
  let stoolCasingType = StoolCasingType.DISABLED;
  if (settings.type === SettingsType.EXTERIOR_WINDOW_CASING_SETTINGS) {
    stoolCasingType = settings.stoolCasingType;
  }
  return (
    sideCasingType !== SideCasingType.DISABLED ||
    headCasingType !== HeadCasingType.NONE ||
    apronCasingType !== ApronCasingType.DISABLED ||
    stoolCasingType !== StoolCasingType.DISABLED
  );
}

export function settingsClipFinish(settings: ExteriorDoorCasingSettings | ExteriorWindowCasingSettings): boolean {
  if (!settings) {
    return false;
  }
  return clipsHorizontally(settings) || clipsVertically(settings);
}

export type ProfileQuery = ProfileAtZ | ProfileMax;

export const PROFILE_MAX_QUERY: ProfileMax = {
  type: "PROFILE_MAX",
};

export interface ProfileMax {
  readonly type: "PROFILE_MAX";
}

/**
 * Objects satisfying the profile at Z interface should be reused
 * as many times as possible to avoid unnecessary object creation.
 */
export interface ProfileAtZ {
  readonly type: "PROFILE_AT_Z";
  readonly z: WorldInches;
}

export function planExtentsFromElevations(
  faceNormal: Vec2,
  frontOutline3?: PolyLine3,
  backOutline3?: PolyLine3,
  query: ProfileQuery = PROFILE_MAX_QUERY,
): Rect2 {
  if (!frontOutline3 || !backOutline3) {
    return rect2ByTwoPoints(0, 0, 0, 0);
  }
  switch (query.type) {
    case "PROFILE_AT_Z":
      return planExtentsAtZ(faceNormal, frontOutline3, backOutline3, query.z);
    case "PROFILE_MAX":
    default:
      return planExtentsMax(faceNormal, frontOutline3, backOutline3);
  }
}

function convexHullFromPolyLine3Intersections(intersections: Segment3PlaneIntersection[]): PolyLine2 {
  const points: number[] = [];
  for (let i = 0; i < intersections.length; i++) {
    const intersection = intersections[i];
    if (intersection.kind === null) {
      continue;
    }
    if (intersection.kind === IntersectionKind.TRANSVERSE) {
      points.push(intersection.intersection.x, intersection.intersection.y);
    } else if (intersection.kind === IntersectionKind.TANGENT) {
      // add the entire segment
      const [x0, y0, , x1, y1] = intersection.segment;
      points.push(x0, y0, x1, y1);
    }
  }
  return polyLine2ConvexHull(polyLine2New(points), DIST_EPSILON);
}

export function rectFromFrontBack(
  faceNormal: Vec2,
  front: PolyLine2 | undefined,
  back: PolyLine2 | undefined,
  lengthTolerance: number = DIST_EPSILON,
  angleTolerance: number = ANGLE_EPSILON,
): Rect2 {
  if (!front || !back || front.length < 4 || back.length < 4) {
    return polyLine2New([]);
  }

  const [fnX, fnY] = faceNormal;

  // ensure the front and back are oriented correctly
  const [feX, feY] = line2Normal(front[0], front[1], front[2], front[3]);

  if (Math.abs(vec2AngleBetween(fnX, fnY, feX, feY)) > angleTolerance) {
    polyLine2Reverse(front);
  }
  const [beX, beY] = line2Normal(back[0], back[1], back[2], back[3]);
  if (Math.abs(vec2AngleBetween(fnX, fnY, beX, beY)) > angleTolerance) {
    polyLine2Reverse(back);
  }

  // choose the longer outline to be the back
  let longer = back;
  let shorter = front;
  let shouldShift = true;
  if (polyLine2Length(front) - polyLine2Length(back) > lengthTolerance) {
    longer = polyLine2Reverse(front);
    shorter = back;
    shouldShift = false;
  }

  // create an oriented rectangle as wide as the longer line and as deep as the distance between lines
  const rect = rect2ByThreePoints(longer[2], longer[3], longer[0], longer[1], shorter[0], shorter[1]);
  if (shouldShift) {
    polyLine2Shift(rect, 2);
  }

  return rect;
}

export function planExtentsAtZ(
  faceNormal: Vec2,
  frontOutline3: PolyLine3,
  backOutline3: PolyLine3,
  z: WorldInches,
): Rect2 {
  return sectionExtents(faceNormal, frontOutline3, backOutline3, planeXYAtZ(z));
}

export function sectionExtents(
  faceNormal: Vec2,
  frontOutline3: PolyLine3,
  backOutline3: PolyLine3,
  cutPlane: Plane | null,
): Rect2 {
  const frontInts = cutPlane ? intersectPolyLine3Plane(cutPlane, frontOutline3) : [];
  const front = convexHullFromPolyLine3Intersections(frontInts);
  const backInts = cutPlane ? intersectPolyLine3Plane(cutPlane, backOutline3) : [];
  const back = convexHullFromPolyLine3Intersections(backInts);
  return rectFromFrontBack(faceNormal, front, back);
}

export function flatExtents(pl: PolyLine3, tolerance: number = 0.01): Line2Segment | undefined {
  const first = pl.slice(0, 2);

  let other;
  for (let i = 3; i < pl.length; i += 3) {
    const x = pl[i];
    const y = pl[i + 1];
    if (Math.abs(x - first[0]) > tolerance || Math.abs(y - first[1]) > tolerance) {
      other = [x, y];
      break;
    }
  }

  if (!other) {
    return undefined;
  }

  const aabb3 = polyLine3AABB3(pl);

  const [lx, ly] = line2Vec([first[0], first[1], other[0], other[1]]);
  const [ax, ay] = line2Vec([aabb3.minX, aabb3.minY, aabb3.maxX, aabb3.maxY]);

  const angle = vec2AngleBetween(lx, ly, ax, ay);
  const clean = angleClean(angle, TWO_PI);

  if (Math.abs(clean - PI) < tolerance) {
    // flipped
    return [aabb3.maxX, aabb3.maxY, aabb3.minX, aabb3.minY];
  } else if (Math.abs(clean) < tolerance) {
    // aligned
    return [aabb3.minX, aabb3.minY, aabb3.maxX, aabb3.maxY];
  } else {
    // not aligned
    const [bx, by] = line2Vec([aabb3.minX, aabb3.maxY, aabb3.maxX, aabb3.minY]);

    const otherAngle = vec2AngleBetween(lx, ly, bx, by);
    const otherClean = angleClean(otherAngle, PI);
    if (Math.abs(otherClean - PI) < tolerance) {
      // flipped
      return [aabb3.minX, aabb3.maxY, aabb3.maxX, aabb3.minY];
    }

    return [aabb3.maxX, aabb3.minY, aabb3.minX, aabb3.maxY];
  }
}

export function planExtentsMax(
  faceNormal: Vec2,
  frontOutline3: PolyLine3,
  backOutline3: PolyLine3,
  tolerance = 0.01,
): Rect2 {
  // flatten the outlines to the same plane, simplify them to a single edge,
  // and generate a rect from their combined extents

  const rect = rectFromFrontBack(
    faceNormal,
    polyLine2New(flatExtents(frontOutline3) ?? []),
    polyLine2New(flatExtents(backOutline3) ?? []),
  );
  if (polyLine2WindingDir(rect) !== WindingDir.CCW) {
    polyLine2Shift(polyLine2Reverse(rect), 1);
  }

  // first edge normal should align to the face normal
  const [fnX, fnY] = faceNormal;
  const [feX, feY] = line2Normal(rect[0], rect[1], rect[2], rect[3]);

  if (Math.abs(angleClean(vec2AngleBetween(fnX, fnY, feX, feY))) > tolerance) {
    // flipped
    polyLine2Shift(rect, 2);
  }

  return rect;
}

export function clipsHorizontally(settings: ExteriorDoorCasingSettings | ExteriorWindowCasingSettings): boolean {
  if (!settings) {
    return false;
  }
  const { sideCasingFinishClipping = FinishClipping.NO_CLIPPING } = settings;
  return sideCasingFinishClipping === FinishClipping.CLIP_FINISH;
}

export function clipsVertically(settings: ExteriorDoorCasingSettings | ExteriorWindowCasingSettings): boolean {
  if (!settings) {
    return false;
  }
  const {
    headCasingFinishClipping = FinishClipping.NO_CLIPPING,
    apronCasingFinishClipping = FinishClipping.NO_CLIPPING,
    stoolCasingFinishClipping = FinishClipping.NO_CLIPPING,
  } = settings;
  return (
    headCasingFinishClipping === FinishClipping.CLIP_FINISH ||
    apronCasingFinishClipping === FinishClipping.CLIP_FINISH ||
    stoolCasingFinishClipping === FinishClipping.CLIP_FINISH
  );
}

export function unionedProfiles(
  profiles: OpeningSide,
  casing: ExteriorWindowCasingSettings | ExteriorDoorCasingSettings | InteriorDoorCasingSettings | null,
): PolyLine2[] {
  const all: PolyLine2[] = [profiles.opening2];
  switch (casing?.type) {
    case "EXTERIOR_WINDOW_CASING_SETTINGS":
    case "EXTERIOR_DOOR_CASING_SETTINGS": {
      if (casing?.sideCasingFinishClipping === FinishClipping.CLIP_FINISH) {
        // use only the outer polylines
        all.push(...profiles.sideCasing.map((poly) => poly[0]));
      }
      if (casing?.headCasingFinishClipping === FinishClipping.CLIP_FINISH) {
        all.push(...profiles.headCasing);
      }
      if (casing?.apronCasingFinishClipping === FinishClipping.CLIP_FINISH && !!profiles?.apronCasing) {
        all.push(profiles.apronCasing);
      }
      if (casing?.stoolCasingFinishClipping === FinishClipping.CLIP_FINISH && !!profiles?.stoolCasing) {
        all.push(profiles.stoolCasing);
      }
      break;
    }
    case "INTERIOR_DOOR_CASING_SETTINGS":
    default: {
      // nothing here yet
      break;
    }
  }

  const validProfiles = all.filter((p) => !!p && p.length > 0);
  if (validProfiles.length > 1) {
    // TODO(irregular-cuts): memoize this calculation
    return polygonUnion({ pls: validProfiles.map((p) => [p]) }).flat();
  }
  return [];
}

export function openingHalfEdgeParam(opening: OpeningResult, halfEdgeLength: number): number {
  switch (opening.type) {
    case "DOOR_V2":
    case "WINDOW_V2":
      return opening.halfEdgeParam;
    case "DOOR":
    case "WINDOW":
      const { param, paramNormalized } = opening;
      if (paramNormalized) {
        let adjustedParam = param;
        if (!opening.wallStripDomain) {
          adjustedParam = 1 - adjustedParam;
        }
        return denormalizeParam(halfEdgeLength, adjustedParam < 0 ? 1 + adjustedParam : adjustedParam, paramNormalized);
      } else if (param > 0) {
        return param;
      } else {
        return halfEdgeLength + param;
      }
    default:
      assertUnreachable(opening);
  }
}
